import * as THREE from 'three';
import {
  ANIMATION_BLOCK_VIEW_ID,
  type BlockOscillator,
  CustomBlockDefinition,
  CustomBlockViewSnapshot,
  MAX_OSCILLATORS,
  RANDOM_BLOCK_VIEW_ID,
  applyOscillatorsWithWavesTo,
  blockTransformMatrix,
  currentWorldAnimTick,
  customBlockId,
  isOscillatorAnimation,
  isVoxelGlowing,
  optimizeCustomBlockVoxels,
  oscillatorAnchorPoint,
  oscillatorWave,
  resolveNoiseSeed,
  resolvePlacementDisplayViewId,
  voxelMaterialGlow,
} from '@shared/protocol';
import {
  buildOptimizedMicroVoxelGeometries,
  isEmptyGeometry,
  isVoxelOpaque,
  type MicroVoxelGeometries,
} from './MicroVoxelGeometry';
import type { GlowLightManager } from './GlowLightManager';
import {
  AnimationTickGate,
  type AnimationFlags,
} from './animationControl';

interface Placement {
  x: number;
  y: number;
  z: number;
  id: number;
  transform: number;
  viewId: number;
  animOrigin: number;
  displayViewId: number;
  meshKey: string;
}

interface VariantMeshes {
  opaque: THREE.InstancedMesh | null;
  transparent: THREE.InstancedMesh | null;
  glowing: THREE.InstancedMesh | null;
}

/** Live oscillator mesh + unit-cube rest positions for in-place deform. */
interface OscillatorLayer {
  mesh: THREE.InstancedMesh;
  rest: Float32Array;
}

interface OscillatorRuntime {
  opaque: OscillatorLayer | null;
  transparent: OscillatorLayer | null;
  glowing: OscillatorLayer | null;
  transform: number;
  matrix: THREE.Matrix4;
  phaseTick: number;
  capacity: number;
  glowVisuals: boolean;
}

interface CustomAppearance {
  gridSize: number;
  voxels: CustomBlockDefinition['voxels'];
  geometry: CustomBlockDefinition['geometry'];
  noise: CustomBlockDefinition['noise'];
  seedKey: number;
}

/**
 * Custom definitions share exposed-face geometry per definition/view/transform.
 * Opaque and transparent parts use separate draws so glass never depth-occludes
 * frames (including frames of instances behind a pane).
 * Oscillator animation deforms vertices in place each tick (visual only).
 */
export class CustomBlockRenderer {
  readonly group = new THREE.Group();

  private readonly definitions = new Map<number, CustomBlockDefinition>();
  private readonly placements = new Map<string, Placement>();
  private readonly meshes = new Map<string, VariantMeshes>();
  private readonly oscillatorRuntimes = new Map<string, OscillatorRuntime>();
  /** Rest-pose geometries (before oscillator deformation). */
  private readonly restGeometries = new Map<string, MicroVoxelGeometries>();
  /** Static (non-oscillator) transformed geometries keyed by meshKey. */
  private readonly geometries = new Map<string, MicroVoxelGeometries>();
  private readonly dirty = new Set<string>();
  private readonly animGate = new AnimationTickGate();
  private worldTick = currentWorldAnimTick();
  private readonly opaqueMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
  });
  private readonly transparentMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
  });
  private readonly glowingMaterial = new THREE.MeshBasicMaterial({
    vertexColors: true,
    toneMapped: false,
  });
  private readonly matrix = new THREE.Matrix4();
  private readonly oscWaves = new Float64Array(MAX_OSCILLATORS);
  private readonly oscAnchors = new Float32Array(MAX_OSCILLATORS * 3);
  private readonly deformScratch = new Float32Array(3);
  private glowVisuals = true;

  constructor(private readonly glowLights: GlowLightManager) {}

  /** When false, glow voxels use lit materials and point lights are cleared. */
  setGlowVisualsEnabled(enabled: boolean): void {
    if (this.glowVisuals === enabled) return;
    this.glowVisuals = enabled;
    for (const key of this.meshes.keys()) this.dirty.add(key);
  }

  setAnimationFlags(flags: AnimationFlags): void {
    if (!this.animGate.setFlags(flags, this.worldTick)) return;
    this.dirtyAnimatedPlacements();
  }

  addDefinitions(definitions: CustomBlockDefinition[]): void {
    for (const definition of definitions) {
      this.definitions.set(definition.id, definition);
      for (const key of [...this.geometries.keys()]) {
        const geometryId = Number(key.split(':')[0]);
        if (geometryId !== definition.id) continue;
        disposeGeometries(this.geometries.get(key)!);
        this.geometries.delete(key);
      }
      for (const key of [...this.restGeometries.keys()]) {
        const geometryId = Number(key.split(':')[0]);
        if (geometryId !== definition.id) continue;
        disposeGeometries(this.restGeometries.get(key)!);
        this.restGeometries.delete(key);
      }
      for (const key of [...this.oscillatorRuntimes.keys()]) {
        if (Number(key.split(':')[0]) === definition.id) {
          this.removeMeshes(key);
        }
      }
      this.refreshDefinitionDisplays(definition.id);
      this.markDefinitionDirty(definition.id);
    }
  }

  setCell(
    x: number,
    y: number,
    z: number,
    type: number,
    transform = 0,
    viewId = 0,
    animOrigin = 0,
  ): void {
    const key = `${x},${y},${z}`;
    const previous = this.placements.get(key);
    if (previous) {
      this.placements.delete(key);
      this.glowLights.removeSource(`custom:${previous.x},${previous.y},${previous.z}`);
      this.dirty.add(previous.meshKey);
    }
    const id = customBlockId(type);
    if (id !== null) {
      const definition = this.definitions.get(id);
      const displayViewId = definition
        ? resolvePlacementDisplayViewId(
          definition.activeViewId,
          definition.animation,
          viewId,
          animOrigin,
          this.worldTick,
        )
        : viewId;
      const meshKey = meshKeyFor(id, viewId, animOrigin, displayViewId, transform, definition);
      this.placements.set(key, {
        x, y, z, id, transform, viewId, animOrigin, displayViewId, meshKey,
      });
      this.dirty.add(meshKey);
    }
  }

  setAnimationTick(worldTick: number): void {
    if (worldTick === this.worldTick) return;
    this.worldTick = worldTick;
    for (const placement of this.placements.values()) {
      const definition = this.definitions.get(placement.id);
      if (!definition) continue;
      const tick = this.animGate.tickFor(definition.animation, worldTick);
      const next = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      const nextKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        next,
        placement.transform,
        definition,
      );
      // Oscillator phase advances in advanceOscillators(); only rebuild when the
      // mesh group / view binding changes.
      if (next === placement.displayViewId && nextKey === placement.meshKey) {
        continue;
      }
      this.dirty.add(placement.meshKey);
      placement.displayViewId = next;
      placement.meshKey = nextKey;
      this.dirty.add(nextKey);
    }
  }

  clear(): void {
    this.placements.clear();
    for (const id of this.meshes.keys()) this.dirty.add(id);
  }

  update(): void {
    for (const key of this.dirty) this.rebuild(key);
    this.dirty.clear();
    this.advanceOscillators();
  }

  /**
   * Drop every mesh variant for this definition (including orphaned keys left
   * behind when Animation mode switches static → oscillator mesh keys).
   */
  private markDefinitionDirty(id: number): void {
    for (const meshKey of this.meshes.keys()) {
      if (Number(meshKey.split(':')[0]) === id) this.dirty.add(meshKey);
    }
    for (const placement of this.placements.values()) {
      if (placement.id === id) this.dirty.add(placement.meshKey);
    }
  }

  private refreshDefinitionDisplays(id: number): void {
    const definition = this.definitions.get(id);
    if (!definition) return;
    for (const placement of this.placements.values()) {
      if (placement.id !== id) continue;
      const previousKey = placement.meshKey;
      const tick = this.animGate.tickFor(definition.animation, this.worldTick);
      placement.displayViewId = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      placement.meshKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        placement.displayViewId,
        placement.transform,
        definition,
      );
      if (previousKey !== placement.meshKey) this.dirty.add(previousKey);
    }
  }

  private dirtyAnimatedPlacements(): void {
    for (const placement of this.placements.values()) {
      const definition = this.definitions.get(placement.id);
      if (!definition?.animation) continue;
      const previousKey = placement.meshKey;
      const tick = this.animGate.tickFor(definition.animation, this.worldTick);
      const next = resolvePlacementDisplayViewId(
        definition.activeViewId,
        definition.animation,
        placement.viewId,
        placement.animOrigin,
        tick,
      );
      const nextKey = meshKeyFor(
        placement.id,
        placement.viewId,
        placement.animOrigin,
        next,
        placement.transform,
        definition,
      );
      this.dirty.add(previousKey);
      placement.displayViewId = next;
      placement.meshKey = nextKey;
      this.dirty.add(nextKey);
    }
  }

  private rebuild(key: string): void {
    const definitionId = Number(key.split(':')[0]);
    const definition = this.definitions.get(definitionId);
    const placements = [...this.placements.values()].filter(
      (item) => item.meshKey === key,
    );
    if (!definition || placements.length === 0) {
      this.removeMeshes(key);
      return;
    }

    const sample = placements[0]!;
    const oscFollow = isOscillatorFollow(definition, sample.viewId);
    if (oscFollow && isOscillatorAnimation(definition.animation)) {
      this.rebuildOscillator(key, definition, placements, sample);
      return;
    }

    this.removeMeshes(key);
    let cached = this.geometries.get(key);
    if (!cached) {
      const appearance = resolveCustomAppearance(definition, sample.displayViewId);
      cached = buildOptimizedMicroVoxelGeometries(
        appearance.geometry?.length > 0
          ? appearance.geometry
          : optimizeCustomBlockVoxels(appearance.voxels, appearance.gridSize),
        appearance.gridSize,
        appearance.noise,
        resolveNoiseSeed(appearance.noise, appearance.seedKey),
      );
      applyPlacementTransform(cached.opaque, sample.transform);
      applyPlacementTransform(cached.transparent, sample.transform);
      applyPlacementTransform(cached.glowing, sample.transform);
      this.geometries.set(key, cached);
    }

    const appearance = resolveCustomAppearance(definition, sample.displayViewId);
    const castShadow = appearance.voxels.some(([, material]) =>
      isVoxelOpaque(material) && !isVoxelGlowing(material),
    );
    const opaque = this.createInstances(
      cached.opaque,
      this.opaqueMaterial,
      placements,
      castShadow,
    );
    const transparent = this.createInstances(
      cached.transparent,
      this.transparentMaterial,
      placements,
      false,
    );
    const glowing = this.createInstances(
      cached.glowing,
      this.glowVisuals ? this.glowingMaterial : this.opaqueMaterial,
      placements,
      !this.glowVisuals && castShadow,
    );
    if (transparent) transparent.renderOrder = 1;
    if (glowing) glowing.renderOrder = this.glowVisuals ? 2 : 0;
    this.meshes.set(key, { opaque, transparent, glowing });
    this.syncGlowLights(placements, appearance);
  }

  private rebuildOscillator(
    key: string,
    definition: CustomBlockDefinition,
    placements: Placement[],
    sample: Placement,
  ): void {
    if (!isOscillatorAnimation(definition.animation)) return;
    const appearance = resolveCustomAppearance(definition, RANDOM_BLOCK_VIEW_ID);
    const castShadow = appearance.voxels.some(([, material]) =>
      isVoxelOpaque(material) && !isVoxelGlowing(material),
    );
    const animTick = this.animGate.tickFor(definition.animation, this.worldTick);
    const phaseTick = definition.animation.sync
      ? animTick
      : animTick - sample.animOrigin;

    let runtime = this.oscillatorRuntimes.get(key);
    const canReuse = runtime
      && runtime.transform === sample.transform
      && runtime.capacity >= placements.length
      && runtime.glowVisuals === this.glowVisuals;

    if (!canReuse) {
      this.removeMeshes(key);
      const rest = this.ensureUnitRest(definition);
      const matrix = placementOrientationMatrix(sample.transform);
      const opaque = this.createOscillatorLayer(
        rest.opaque,
        sample.transform,
        placements,
        this.opaqueMaterial,
        castShadow,
        definition.animation.oscillators,
      );
      const transparent = this.createOscillatorLayer(
        rest.transparent,
        sample.transform,
        placements,
        this.transparentMaterial,
        false,
        definition.animation.oscillators,
      );
      const glowing = this.createOscillatorLayer(
        rest.glowing,
        sample.transform,
        placements,
        this.glowVisuals ? this.glowingMaterial : this.opaqueMaterial,
        !this.glowVisuals && castShadow,
        definition.animation.oscillators,
      );
      if (transparent) transparent.mesh.renderOrder = 1;
      if (glowing) glowing.mesh.renderOrder = this.glowVisuals ? 2 : 0;
      runtime = {
        opaque,
        transparent,
        glowing,
        transform: sample.transform,
        matrix,
        phaseTick: Number.NaN,
        capacity: placements.length,
        glowVisuals: this.glowVisuals,
      };
      this.oscillatorRuntimes.set(key, runtime);
    } else {
      this.writeInstanceMatrices(runtime!, placements);
    }

    this.meshes.set(key, {
      opaque: runtime!.opaque?.mesh ?? null,
      transparent: runtime!.transparent?.mesh ?? null,
      glowing: runtime!.glowing?.mesh ?? null,
    });
    this.syncGlowLights(placements, appearance);

    if (runtime!.phaseTick !== phaseTick) {
      runtime!.phaseTick = phaseTick;
      this.deformOscillatorRuntime(
        runtime!,
        definition.animation.oscillators,
        phaseTick,
      );
    }
  }

  private advanceOscillators(): void {
    if (this.oscillatorRuntimes.size === 0) return;
    for (const [key, runtime] of this.oscillatorRuntimes) {
      const definition = this.definitions.get(Number(key.split(':')[0]));
      if (!definition || !isOscillatorAnimation(definition.animation)) continue;
      if (!this.animGate.isKindEnabled('oscillator')) continue;

      const placements = [...this.placements.values()].filter(
        (item) => item.meshKey === key,
      );
      if (placements.length === 0) continue;
      const sample = placements[0]!;
      const animTick = this.animGate.tickFor(definition.animation, this.worldTick);
      const phaseTick = definition.animation.sync
        ? animTick
        : animTick - sample.animOrigin;
      if (phaseTick === runtime.phaseTick) continue;
      runtime.phaseTick = phaseTick;
      this.deformOscillatorRuntime(
        runtime,
        definition.animation.oscillators,
        phaseTick,
      );
    }
  }

  private ensureUnitRest(definition: CustomBlockDefinition): MicroVoxelGeometries {
    const restKey = restGeometryKey(definition.id);
    let rest = this.restGeometries.get(restKey);
    if (rest) return rest;
    const appearance = resolveCustomAppearance(definition, RANDOM_BLOCK_VIEW_ID);
    rest = buildOptimizedMicroVoxelGeometries(
      appearance.geometry?.length > 0
        ? appearance.geometry
        : optimizeCustomBlockVoxels(appearance.voxels, appearance.gridSize),
      appearance.gridSize,
      appearance.noise,
      resolveNoiseSeed(appearance.noise, appearance.seedKey),
    );
    this.restGeometries.set(restKey, rest);
    return rest;
  }

  private createOscillatorLayer(
    source: THREE.BufferGeometry,
    transform: number,
    placements: Placement[],
    material: THREE.Material,
    castShadow: boolean,
    oscillators: readonly BlockOscillator[],
  ): OscillatorLayer | null {
    if (isEmptyGeometry(source)) return null;
    const restAttr = source.getAttribute('position');
    if (!restAttr) return null;
    const rest = new Float32Array(restAttr.array.length);
    rest.set(restAttr.array as ArrayLike<number>);

    const geometry = source.clone();
    applyPlacementTransform(geometry, transform);
    // Stable padded bounds — deform stays inside; avoid per-frame sphere recompute.
    geometry.computeBoundingSphere();
    const sphere = geometry.boundingSphere
      ?? new THREE.Sphere(new THREE.Vector3(0.5, 0.5, 0.5), 1);
    sphere.radius = Math.max(0.01, sphere.radius + oscillatorBoundPad(oscillators));
    geometry.boundingSphere = sphere;

    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    placements.forEach((placement, index) => {
      this.matrix.makeTranslation(placement.x, placement.y, placement.z);
      mesh.setMatrixAt(index, this.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = castShadow;
    mesh.receiveShadow = castShadow && material !== this.glowingMaterial;
    if (material === this.glowingMaterial) mesh.receiveShadow = false;
    mesh.frustumCulled = true;
    this.group.add(mesh);
    return { mesh, rest };
  }

  private writeInstanceMatrices(
    runtime: OscillatorRuntime,
    placements: Placement[],
  ): void {
    for (const layer of [runtime.opaque, runtime.transparent, runtime.glowing]) {
      if (!layer) continue;
      const mesh = layer.mesh;
      if (mesh.count !== placements.length) {
        // InstancedMesh count can shrink; growing beyond capacity needs rebuild.
        mesh.count = placements.length;
      }
      placements.forEach((placement, index) => {
        this.matrix.makeTranslation(placement.x, placement.y, placement.z);
        mesh.setMatrixAt(index, this.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  private deformOscillatorRuntime(
    runtime: OscillatorRuntime,
    oscillators: readonly BlockOscillator[],
    phaseTick: number,
  ): void {
    const count = Math.min(oscillators.length, MAX_OSCILLATORS);
    for (let i = 0; i < count; i++) {
      const oscillator = oscillators[i]!;
      this.oscWaves[i] = oscillatorWave(oscillator, phaseTick);
      const [ax, ay, az] = oscillatorAnchorPoint(oscillator.anchor);
      const ai = i * 3;
      this.oscAnchors[ai] = ax;
      this.oscAnchors[ai + 1] = ay;
      this.oscAnchors[ai + 2] = az;
    }
    const waves = this.oscWaves.subarray(0, count);
    const anchors = this.oscAnchors.subarray(0, count * 3);
    const elements = runtime.matrix.elements;

    for (const layer of [runtime.opaque, runtime.transparent, runtime.glowing]) {
      if (!layer) continue;
      const pos = layer.mesh.geometry.getAttribute('position');
      if (!pos) continue;
      const out = pos.array as Float32Array;
      const rest = layer.rest;
      for (let v = 0; v < rest.length; v += 3) {
        applyOscillatorsWithWavesTo(
          rest[v]!,
          rest[v + 1]!,
          rest[v + 2]!,
          oscillators,
          waves,
          anchors,
          this.deformScratch,
          0,
        );
        const x = this.deformScratch[0]!;
        const y = this.deformScratch[1]!;
        const z = this.deformScratch[2]!;
        out[v] = elements[0]! * x + elements[4]! * y + elements[8]! * z + elements[12]!;
        out[v + 1] = elements[1]! * x + elements[5]! * y + elements[9]! * z + elements[13]!;
        out[v + 2] = elements[2]! * x + elements[6]! * y + elements[10]! * z + elements[14]!;
      }
      pos.needsUpdate = true;
    }
  }

  private syncGlowLights(
    placements: Placement[],
    appearance: CustomAppearance,
  ): void {
    let maxGlow = 0;
    let glowColor = '#ffffff';
    for (const [, material] of appearance.voxels) {
      const glow = voxelMaterialGlow(material);
      if (glow > maxGlow) {
        maxGlow = glow;
        if (typeof material === 'string' && material.length >= 7) {
          glowColor = material.slice(0, 7);
        }
      }
    }
    for (const placement of placements) {
      const key = `custom:${placement.x},${placement.y},${placement.z}`;
      if (this.glowVisuals && maxGlow > 0) {
        this.glowLights.setSource(
          key,
          placement.x,
          placement.y,
          placement.z,
          glowColor,
          maxGlow,
        );
      } else {
        this.glowLights.removeSource(key);
      }
    }
  }

  private createInstances(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    placements: Placement[],
    castShadow: boolean,
  ): THREE.InstancedMesh | null {
    if (isEmptyGeometry(geometry)) return null;
    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    placements.forEach((placement, index) => {
      this.matrix.makeTranslation(placement.x, placement.y, placement.z);
      mesh.setMatrixAt(index, this.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = castShadow;
    mesh.receiveShadow = !castShadow ? false : true;
    if (material === this.glowingMaterial) mesh.receiveShadow = false;
    this.group.add(mesh);
    return mesh;
  }

  private removeMeshes(key: string): void {
    const runtime = this.oscillatorRuntimes.get(key);
    if (runtime) {
      for (const layer of [runtime.opaque, runtime.transparent, runtime.glowing]) {
        if (!layer) continue;
        this.group.remove(layer.mesh);
        layer.mesh.geometry.dispose();
        layer.mesh.dispose();
      }
      this.oscillatorRuntimes.delete(key);
      this.meshes.delete(key);
      return;
    }

    const entry = this.meshes.get(key);
    if (!entry) return;
    for (const mesh of [entry.opaque, entry.transparent, entry.glowing]) {
      if (!mesh) continue;
      this.group.remove(mesh);
      mesh.dispose();
    }
    this.meshes.delete(key);
  }
}

function isOscillatorFollow(
  definition: CustomBlockDefinition,
  placementViewId: number,
): boolean {
  return placementViewId <= RANDOM_BLOCK_VIEW_ID
    && definition.activeViewId === ANIMATION_BLOCK_VIEW_ID
    && isOscillatorAnimation(definition.animation);
}

function meshKeyFor(
  id: number,
  placementViewId: number,
  animOrigin: number,
  displayViewId: number,
  transform: number,
  definition: CustomBlockDefinition | undefined,
): string {
  if (definition && isOscillatorFollow(definition, placementViewId)) {
    return definition.animation.sync
      ? `${id}:osc:${transform}`
      : `${id}:osc:${transform}:${animOrigin}`;
  }
  return `${id}:${displayViewId}:${transform}`;
}

function restGeometryKey(id: number): string {
  return `${id}:rest`;
}

/** Same orientation matrix as applyPlacementTransform (unit cube → placed). */
function placementOrientationMatrix(transform: number): THREE.Matrix4 {
  const values = blockTransformMatrix(transform);
  const orientation = new THREE.Matrix4().set(
    values[0], values[1], values[2], 0,
    values[3], values[4], values[5], 0,
    values[6], values[7], values[8], 0,
    0, 0, 0, 1,
  );
  return new THREE.Matrix4()
    .makeTranslation(0.5, 0.5, 0.5)
    .multiply(orientation)
    .multiply(new THREE.Matrix4().makeTranslation(-0.5, -0.5, -0.5));
}

/** Conservative extra radius so frustum culling survives max oscillator travel. */
function oscillatorBoundPad(oscillators: readonly BlockOscillator[]): number {
  let pad = 0;
  for (const oscillator of oscillators) {
    pad += Math.abs(oscillator.amplitude) * 1.75;
  }
  return pad;
}

function resolveCustomAppearance(
  definition: CustomBlockDefinition,
  viewId: number,
): CustomAppearance {
  if (viewId > RANDOM_BLOCK_VIEW_ID) {
    const snapshot = definition.snapshots?.find((item) => item.id === viewId);
    if (snapshot) return appearanceFromSnapshot(snapshot);
  }
  return {
    gridSize: definition.gridSize,
    voxels: definition.voxels,
    geometry: definition.geometry,
    noise: definition.noise,
    seedKey: definition.id,
  };
}

function appearanceFromSnapshot(snapshot: CustomBlockViewSnapshot): CustomAppearance {
  return {
    gridSize: snapshot.gridSize,
    voxels: snapshot.voxels,
    geometry: snapshot.geometry,
    noise: snapshot.noise,
    seedKey: snapshot.id,
  };
}

function disposeGeometries(geometries: MicroVoxelGeometries): void {
  geometries.opaque.dispose();
  geometries.transparent.dispose();
  geometries.glowing.dispose();
}

function applyPlacementTransform(
  geometry: THREE.BufferGeometry,
  transform: number,
): void {
  if (isEmptyGeometry(geometry)) return;
  const values = blockTransformMatrix(transform);
  const orientation = new THREE.Matrix4().set(
    values[0], values[1], values[2], 0,
    values[3], values[4], values[5], 0,
    values[6], values[7], values[8], 0,
    0, 0, 0, 1,
  );
  const matrix = new THREE.Matrix4()
    .makeTranslation(0.5, 0.5, 0.5)
    .multiply(orientation)
    .multiply(new THREE.Matrix4().makeTranslation(-0.5, -0.5, -0.5));
  geometry.applyMatrix4(matrix);
  if (orientation.determinant() < 0) reverseTriangleWinding(geometry);
}

function reverseTriangleWinding(geometry: THREE.BufferGeometry): void {
  const index = geometry.getIndex();
  if (!index) return;
  for (let offset = 0; offset < index.count; offset += 3) {
    const second = index.getX(offset + 1);
    index.setX(offset + 1, index.getX(offset + 2));
    index.setX(offset + 2, second);
  }
  index.needsUpdate = true;
}
